import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft,
  faDownload,
  faShare,
  faStar,
  faCalendar,
  faUser,
  faHeart,
  faBrain,
  faEye,
  faMagic,
  faSpinner,
  faChartLine,
  faLightbulb,
  faQuoteLeft
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { apiService } from '../utils/api';
import AnalysisFeedback from '../components/AnalysisFeedback';

interface AnalysisResult {
  id: string;
  analysisType: string;
  personalInfo: {
    name: string;
    birthDate: string;
    additionalInfo?: any;
  };
  results: {
    title: string;
    value: string | number;
    description: string;
    significance: 'high' | 'medium' | 'low';
    category: string;
  }[];
  insights: {
    title: string;
    content: string;
    type: 'strength' | 'challenge' | 'opportunity' | 'guidance';
  }[];
  recommendations: string[];
  summary: string;
  createdAt: string;
}

const AnalysisResultPage: React.FC = () => {
  const { t } = useTranslation();
  const { analysisId } = useParams<{ analysisId: string }>();
  const navigate = useNavigate();
  const [analysisData, setAnalysisData] = useState<AnalysisResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const reportRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchAnalysisResult = async () => {
      try {
        // Simulate API call - replace with actual API endpoint
        setLoading(true);
        
        // Extract analysis type from ID for demo
        const analysisType = analysisId?.includes('personal') ? 'Kişisel Numeroloji' :
                            analysisId?.includes('relationship') ? 'İlişki Numerolojisi' :
                            analysisId?.includes('astro') ? 'Astro Numeroloji' :
                            analysisId?.includes('healing') ? 'Sayılarla Şifa' :
                            analysisId?.includes('psychological') ? 'Psikolojik Numeroloji' :
                            'AI Numeroloji';
        
        // Mock data for demonstration - this will be replaced with actual API call
        const mockData: AnalysisResult = {
          id: analysisId || '1',
          analysisType: `AI ${analysisType}`,
          personalInfo: {
            name: 'Örnek Kullanıcı',
            birthDate: '15.03.1990'
          },
          results: [
            {
              title: 'Hayat Yolu Sayısı',
              value: 7,
              description: 'Araştırmacı ve düşünür kişiliğinizi temsil eder. İç gözlem ve bilgi arayışınız yaşamınızın merkezindedir.',
              significance: 'high',
              category: 'core'
            },
            {
              title: 'Kader Sayısı',
              value: 3,
              description: 'Yaratıcı ifade yeteneğiniz ve sosyal beceriniz ön plandadır. İletişim konusunda doğal yetenekleriniz vardır.',
              significance: 'high',
              category: 'destiny'
            },
            {
              title: 'Ruh Sayısı',
              value: 11,
              description: 'Güçlü sezgi ve ruhsal farkındalık. İnsanlara ilham verme yeteneğiniz bulunmaktadır.',
              significance: 'medium',
              category: 'soul'
            },
            {
              title: 'Kişilik Sayısı',
              value: 5,
              description: 'Özgür ruhlu ve maceracı yapınız. Değişimi ve yeniliği seven bir kişiliğiniz var.',
              significance: 'medium',
              category: 'personality'
            }
          ],
          insights: [
            {
              title: 'Güçlü Yönleriniz',
              content: 'Analitik düşünce yapınız ve derin sezgisel yetenekleriniz kombinasyonu size özel bir perspektif kazandırıyor.',
              type: 'strength'
            },
            {
              title: 'Gelişim Alanlarınız',
              content: 'Sosyal etkileşimlerde daha açık olmak, duygusal ifadenizi geliştirmek size fayda sağlayacaktır.',
              type: 'challenge'
            },
            {
              title: 'Fırsatlarınız',
              content: 'Eğitim, danışmanlık veya araştırma alanlarında büyük başarılar elde edebilirsiniz.',
              type: 'opportunity'
            }
          ],
          recommendations: [
            'Günlük meditasyon pratiği yaparak iç sesinizi güçlendirin',
            'Yaratıcı projeler geliştirerek ifade yeteneğinizi kullanın',
            'Sosyal aktivitelere katılarak iletişim becerilerinizi geliştirin',
            'Öğrenmeye ayırdığınız zamanı artırın ve yeni alanlarda uzmanlaşın'
          ],
          summary: 'Analitik zeka ve güçlü sezgi kombinasyonuna sahip bir kişiliğiniz var. Yaratıcı ifade yeteneğinizi geliştirerek ve sosyal becerilerinizi güçlendirerek tam potansiyelinize ulaşabilirsiniz.',
          createdAt: new Date().toISOString()
        };

        // In real implementation, you would make an actual API call like:
        /*
        const response = await fetch(`/api/analysis-results/${analysisId}`, {
          headers: {
            'Authorization': `Bearer ${userToken}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch analysis result');
        }
        
        const analysisData = await response.json();
        */

        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 1500));
        setAnalysisData(mockData);
      } catch (err) {
        setError('Analiz sonuçları yüklenirken bir hata oluştu.');
      } finally {
        setLoading(false);
      }
    };

    fetchAnalysisResult();
  }, [analysisId]);

  const getSignificanceColor = (significance: string) => {
    switch (significance) {
      case 'high': return '#dc2626';
      case 'medium': return '#d97706';
      case 'low': return '#059669';
      default: return '#6b7280';
    }
  };

  const getInsightIcon = (type: string) => {
    switch (type) {
      case 'strength': return faStar;
      case 'challenge': return faBrain;
      case 'opportunity': return faEye;
      case 'guidance': return faLightbulb;
      default: return faQuoteLeft;
    }
  };

  const getInsightColor = (type: string) => {
    switch (type) {
      case 'strength': return 'linear-gradient(135deg, #10b981, #059669)';
      case 'challenge': return 'linear-gradient(135deg, #f59e0b, #d97706)';
      case 'opportunity': return 'linear-gradient(135deg, #3b82f6, #2563eb)';
      case 'guidance': return 'linear-gradient(135deg, #8b5cf6, #7c3aed)';
      default: return 'linear-gradient(135deg, #6b7280, #4b5563)';
    }
  };

  const handleDownload = async () => {
    if (!analysisData || !reportRef.current) return;
    
    setIsDownloading(true);
    
    try {
      // Create a clone of the report for PDF generation
      const element = reportRef.current;
      
      // Capture the element as canvas
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: element.scrollWidth,
        height: element.scrollHeight,
        scrollX: 0,
        scrollY: 0
      });
      
      const imgData = canvas.toDataURL('image/png');
      
      // Create PDF
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      
      // Calculate scaling to fit the page
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const scaledWidth = imgWidth * ratio;
      const scaledHeight = imgHeight * ratio;
      
      // Center the image on the page
      const x = (pdfWidth - scaledWidth) / 2;
      const y = (pdfHeight - scaledHeight) / 2;
      
      // If content is taller than one page, handle multiple pages
      if (scaledHeight > pdfHeight) {
        let position = 0;
        const pageHeight = pdfHeight;
        
        while (position < scaledHeight) {
          const remainingHeight = scaledHeight - position;
          const currentPageHeight = Math.min(pageHeight, remainingHeight);
          
          pdf.addImage(
            imgData,
            'PNG',
            x,
            -position,
            scaledWidth,
            scaledHeight
          );
          
          position += pageHeight;
          
          if (position < scaledHeight) {
            pdf.addPage();
          }
        }
      } else {
        pdf.addImage(imgData, 'PNG', x, y, scaledWidth, scaledHeight);
      }
      
      // Generate filename
      const filename = `Kairoscope-${analysisData.analysisType.replace(/\s+/g, '-')}-${new Date().toLocaleDateString('tr-TR').replace(/\./g, '-')}.pdf`;
      
      // Download the PDF
      pdf.save(filename);
      
    } catch (error) {
      console.error('PDF generation failed:', error);
      alert('PDF oluşturulurken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleShare = () => {
    if (!analysisData) return;
    
    const shareText = `🔮 ${analysisData.analysisType} Raporum\n\n${analysisData.summary}\n\n✨ Kairoscope ile numeroloji keşfine devam edin!`;
    const shareUrl = window.location.href;
    
    // WhatsApp sharing
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText + '\n\n' + shareUrl)}`;
    
    // Open WhatsApp
    window.open(whatsappUrl, '_blank');
  };

  const pageStyle: React.CSSProperties = {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f5f3ff 0%, #e9d5ff 50%, #f5f3ff 100%)',
    padding: '40px 20px'
  };

  const containerStyle: React.CSSProperties = {
    maxWidth: '1000px',
    margin: '0 auto'
  };

  const headerStyle: React.CSSProperties = {
    background: 'white',
    borderRadius: '16px',
    padding: '2rem',
    marginBottom: '2rem',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
    border: '1px solid rgba(168, 85, 247, 0.1)'
  };

  const cardStyle: React.CSSProperties = {
    background: 'white',
    borderRadius: '16px',
    padding: '1.5rem',
    marginBottom: '1.5rem',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    border: '1px solid rgba(168, 85, 247, 0.1)',
    transition: 'all 0.3s ease'
  };

  if (loading) {
    return (
      <div style={{
        ...pageStyle,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '3rem', color: '#8b5cf6', marginBottom: '1rem' }} />
          <p style={{ fontSize: '1.125rem', color: '#6b7280' }}>Analiz sonuçlarınız hazırlanıyor...</p>
        </div>
      </div>
    );
  }

  if (error || !analysisData) {
    return (
      <div style={pageStyle}>
        <div style={containerStyle}>
          <div style={{ textAlign: 'center', color: '#dc2626' }}>
            <h2>Hata</h2>
            <p>{error || 'Analiz bulunamadı'}</p>
            <button onClick={() => navigate('/')} style={{ marginTop: '1rem', padding: '8px 16px' }}>
              Ana Sayfaya Dön
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <div style={containerStyle} ref={reportRef}>
        {/* Header */}
        <div style={headerStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
            <button
              onClick={() => navigate(-1)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                background: 'transparent',
                border: 'none',
                color: '#7c3aed',
                cursor: 'pointer',
                fontSize: '1rem'
              }}
            >
              <FontAwesomeIcon icon={faArrowLeft} />
              <span>Geri Dön</span>
            </button>
            
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                onClick={handleDownload}
                disabled={isDownloading}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  background: isDownloading ? '#9ca3af' : 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px 16px',
                  cursor: isDownloading ? 'not-allowed' : 'pointer',
                  opacity: isDownloading ? 0.7 : 1,
                  transition: 'all 0.3s ease'
                }}
              >
                <FontAwesomeIcon icon={isDownloading ? faSpinner : faDownload} spin={isDownloading} />
                <span>{isDownloading ? 'İndiriliyor...' : 'PDF İndir'}</span>
              </button>
              
              <button
                onClick={handleShare}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  background: 'transparent',
                  color: '#25D366',
                  border: '1px solid #25D366',
                  borderRadius: '8px',
                  padding: '8px 16px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#25D366';
                  e.currentTarget.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = '#25D366';
                }}
              >
                <FontAwesomeIcon icon={faWhatsapp} />
                <span>WhatsApp</span>
              </button>
            </div>
          </div>
          
          <div style={{ textAlign: 'center' }}>
            <h1 style={{ fontSize: '2rem', fontWeight: 'bold', color: '#374151', marginBottom: '0.5rem' }}>
              {analysisData.analysisType} Raporu
            </h1>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '2rem', color: '#6b7280', fontSize: '0.875rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <FontAwesomeIcon icon={faUser} />
                <span>{analysisData.personalInfo.name}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <FontAwesomeIcon icon={faCalendar} />
                <span>{analysisData.personalInfo.birthDate}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <FontAwesomeIcon icon={faChartLine} />
                <span>{new Date(analysisData.createdAt).toLocaleDateString('tr-TR')}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Summary */}
        <div style={cardStyle}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#374151', marginBottom: '1rem' }}>
            Genel Değerlendirme
          </h2>
          <p style={{ color: '#6b7280', lineHeight: 1.6, fontSize: '1.125rem' }}>
            {analysisData.summary}
          </p>
        </div>

        {/* Results Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
          {analysisData.results.map((result, index) => (
            <div
              key={index}
              style={{
                ...cardStyle,
                marginBottom: 0,
                borderLeft: `4px solid ${getSignificanceColor(result.significance)}`
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-5px)';
                e.currentTarget.style.boxShadow = '0 10px 25px -5px rgba(0, 0, 0, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#374151', margin: 0 }}>
                  {result.title}
                </h3>
                <div style={{
                  background: getSignificanceColor(result.significance),
                  color: 'white',
                  padding: '4px 12px',
                  borderRadius: '20px',
                  fontSize: '2rem',
                  fontWeight: 'bold',
                  minWidth: '60px',
                  textAlign: 'center'
                }}>
                  {result.value}
                </div>
              </div>
              <p style={{ color: '#6b7280', lineHeight: 1.6 }}>
                {result.description}
              </p>
            </div>
          ))}
        </div>

        {/* Insights */}
        <div style={cardStyle}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#374151', marginBottom: '1.5rem' }}>
            Detaylı İçgörüler
          </h2>
          <div style={{ display: 'grid', gap: '1rem' }}>
            {analysisData.insights.map((insight, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  gap: '1rem',
                  padding: '1rem',
                  background: 'linear-gradient(135deg, #f9fafb, #f3f4f6)',
                  borderRadius: '12px',
                  border: '1px solid #e5e7eb'
                }}
              >
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  background: getInsightColor(insight.type),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <FontAwesomeIcon icon={getInsightIcon(insight.type)} style={{ color: 'white', fontSize: '1.25rem' }} />
                </div>
                <div>
                  <h4 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
                    {insight.title}
                  </h4>
                  <p style={{ color: '#6b7280', lineHeight: 1.6, margin: 0 }}>
                    {insight.content}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recommendations */}
        <div style={cardStyle}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#374151', marginBottom: '1.5rem' }}>
            <FontAwesomeIcon icon={faLightbulb} style={{ marginRight: '0.5rem', color: '#f59e0b' }} />
            Kişisel Öneriler
          </h2>
          <div style={{ display: 'grid', gap: '0.75rem' }}>
            {analysisData.recommendations.map((recommendation, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.75rem',
                  padding: '1rem',
                  background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
                  borderRadius: '8px',
                  border: '1px solid #f59e0b'
                }}
              >
                <div style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  background: '#f59e0b',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.875rem',
                  fontWeight: 'bold',
                  flexShrink: 0
                }}>
                  {index + 1}
                </div>
                <p style={{ color: '#92400e', lineHeight: 1.6, margin: 0 }}>
                  {recommendation}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Analysis Feedback */}
        <AnalysisFeedback 
          analysisId={analysisData.id}
          analysisType={analysisData.analysisType}
          onFeedbackSubmitted={() => {
            console.log('Feedback submitted for analysis:', analysisData.id);
          }}
        />

        {/* Footer */}
        <div style={{
          textAlign: 'center',
          padding: '2rem',
          color: '#6b7280',
          fontSize: '0.875rem'
        }}>
          <p>Bu rapor Kairoscope AI tarafından {new Date(analysisData.createdAt).toLocaleDateString('tr-TR')} tarihinde oluşturulmuştur.</p>
          <p style={{ marginTop: '0.5rem' }}>
            © 2025 Kairoscope Numeroloji - AI Powered. Made with ❤️
          </p>
        </div>
      </div>
    </div>
  );
};

export default AnalysisResultPage; 